import { useCallback, useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { ExhPatientConsentData } from "types/consent";
import { Consent } from "types/patient";

import { convertExhConsentsToConsent } from "../utils/consentConverters";

interface UseExhConsentsResult {
  consents: Consent | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching patient consents from ExtraHorizon
 *
 * Fetches all consent documents for a patient, sorts by date descending,
 * and converts to app Consent format with history.
 *
 * Uses the patient-consent schema which is intercepted and redirected to patient-consents-endpoint
 */
export const useExhConsents = (patientId: string | undefined): UseExhConsentsResult => {
  const [consents, setConsents] = useState<Consent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { showBoundary: onError } = useErrorBoundary();

  const loadConsents = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      setConsents(null);
      return;
    }

    if (!isExhClientInitialized()) {
      setLoading(false);
      setError(new Error("ExtraHorizon client not initialized"));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const client = getExhClient();

      // Fetch consent documents for this patient
      // Uses patient-consent schema which is intercepted and redirected to patient-consents-endpoint
      // ExH schema uses camelCase property names (alphanumeric only, no underscores)
      const rql = rqlBuilder().eq("data.patient_id", patientId).sort("-data.changed_date").build();
      const result = await client.data.documents.find("patient-consent", {
        rql,
      });
      const exhConsentDocs = (result.data || []) as ExhPatientConsentData[];

      // Convert ExH format to Consent format with history
      // Use patient creation as default date if no consents exist
      const defaultDate = new Date().toISOString();
      const consentData = convertExhConsentsToConsent(exhConsentDocs, defaultDate);
      setConsents(consentData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch consents");
      setError(error);
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [patientId, onError]);

  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  return { consents, loading, error, refetch: loadConsents };
};
